import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FieldProps } from "formik";
import { css } from "styled-components";
import { Box } from "rebass";
import { toastr } from "react-redux-toastr";

import { getIsPlaying } from "../../../store/modules/audio/selectors";
import { getFontSize } from "../../../store/modules/display/selectors";
import { Mode } from "../../../store/modules/library/selectors";

import { HighlightFind } from "../Highlightable/HightlightFind";
import { HighlightRange } from "../Highlightable/HighlightRange";
import { RecordText } from "../styled/RecordText";

import { DEFAULT_TEXT } from "./formSettings";
import { audioPause } from "../../../store/modules/audio/actions";

interface IProps extends FieldProps {
  mode: Mode;
}

const disableModes = [Mode.view];

const textareaCSS = css`
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  resize: none;
  caret-color: #000;
`;

export const RecordInput: React.FC<IProps> = (
  {
    field,
    form: { isSubmitting},
    mode,
  }
) => {
  const dispatch = useDispatch();
  const fontSize = useSelector(getFontSize);
  const isPlaying = useSelector(getIsPlaying);

  /* create text from value */
  const text = useMemo(() => (
    field.value.replace(/\n$/, "\n\n") || DEFAULT_TEXT
  ), [field.value]);
  /* auto stop playing if editing */
  useEffect(() => {
    if (mode === Mode.edit && isPlaying) {
      toastr.warning("Audio Stopped.", "Toggle edit mode.");
      dispatch(audioPause());
    }
  }, [mode, isPlaying, dispatch]);

  return (
    <Box
      flex={1}
      fontSize={fontSize}
      css="position: relative;"
    >
      {/* Next block set width, height and visible text. position: static */}
      <RecordText color="black">
        {text}
      </RecordText>

      {
        isPlaying
          ? <HighlightRange text={text} />
          : <HighlightFind text={text} />
      }

      {
        !disableModes.includes(mode) && (
          <RecordText
            as="textarea"
            {...field}
            placeholder={DEFAULT_TEXT}
            disabled={isSubmitting}
            css={textareaCSS}
          />
        )
      }
    </Box>
  );
};
